/**
 * Token Usage Chart Component
 *
 * Displays daily token usage as a line chart using recharts.
 * Shows historical token consumption with area fill and tooltips.
 */

import React from 'react';
import { Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart } from 'recharts';
import { TokenHistoryPoint } from '../../../../../types/metrics';

interface TokenUsageChartProps {
  data: TokenHistoryPoint[];
}

export const TokenUsageChart: React.FC<TokenUsageChartProps> = ({ data }) => {
  // Format data for recharts
  const chartData = data.map((point) => ({
    date: new Date(point.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    tokens: point.tokens,
  }));

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(0)}K`;
    return tokens.toString();
  };

  return (
    <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] h-[442px]">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Uso de Tokens</h3>
        <p className="text-sm text-[#888888]">Histórico diário de consumo</p>
      </div>

      <ResponsiveContainer width="100%" height={330}>
        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="tokenGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F4CC04" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#F4CC04" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#333333" />
          <XAxis dataKey="date" stroke="#888888" style={{ fontSize: '12px' }} />
          <YAxis stroke="#888888" style={{ fontSize: '12px' }} tickFormatter={formatTokens} />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333333',
              borderRadius: '8px',
              color: '#ffffff',
            }}
            labelStyle={{ color: '#cccccc' }}
            formatter={(value: number) => [formatTokens(value), 'Tokens']}
          />
          <Area
            type="monotone"
            dataKey="tokens"
            stroke="#F4CC04"
            strokeWidth={2}
            fill="url(#tokenGradient)"
            animationDuration={500}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
